import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Lock as LockIcon,
  CreditCard as CreditCardIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectBankAccount, selectBankFeatures } from '@/store/slices/bankAccountSlice';
import type { Saldo } from '@/types/bank';

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

export default function ContaSaldoPage() {
  const account = useAppSelector(selectBankAccount);
  const features = useAppSelector(selectBankFeatures);

  const [loading, setLoading] = useState(false);
  const [saldo, setSaldo] = useState<Saldo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBalance, setShowBalance] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchSaldo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<Saldo>('/bank/saldo');

      if (response.success && response.data) {
        setSaldo(response.data);
        setLastUpdate(new Date());
      } else {
        setError(response.error?.message || 'Erro ao carregar saldo');
      }
    } catch (err: any) {
      setError(errMsg(err, 'Erro ao carregar saldo'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSaldo(null);
    setLastUpdate(null);
    fetchSaldo();
  }, [fetchSaldo, account?.id]);

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const maskValue = (value: string) => {
    return showBalance ? value : 'R$ ••••••';
  };

  const limite = saldo?.limite ?? 0;
  const bloqueado = saldo?.bloqueado ?? 0;
  const disponivel = saldo?.disponivel ?? 0;
  // Total informado pelo backend; se ausente, saldo disponível + limite
  const total = saldo?.total ?? disponivel + limite;
  const esconderLimite = account?.provider === 'efi' && limite === 0;

  const atualizadoEm = saldo?.atualizadoEm
    ? (() => {
        const d = new Date(saldo.atualizadoEm);
        return isNaN(d.getTime()) ? null : format(d, 'dd/MM/yyyy HH:mm');
      })()
    : null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Saldo da Conta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte o saldo disponível da conta selecionada
          </Typography>
          {account && (
            <Chip
              size="small"
              variant="outlined"
              icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
              label={`${account.providerLabel} · ${account.label}${account.companyName ? ` — ${account.companyName}` : ''}`}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={showBalance ? 'Ocultar valores' : 'Mostrar valores'}>
            <IconButton onClick={() => setShowBalance(!showBalance)}>
              {showBalance ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar saldo">
            <span>
              <IconButton onClick={fetchSaldo} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {!account && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhuma conta bancária selecionada. Escolha uma conta no seletor do topo da página
          (Empresa · Banco). Sem seleção, o sistema usa a conta padrão da empresa.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !saldo ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Saldo Principal */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Saldo em Conta{account ? ` — ${account.providerLabel} ${account.label}` : ''}
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {maskValue(formatCurrency(disponivel))}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                {atualizadoEm
                  ? `Posição do banco em: ${atualizadoEm}`
                  : lastUpdate
                    ? `Atualizado em: ${lastUpdate.toLocaleString('pt-BR')}`
                    : ''}
                {atualizadoEm && lastUpdate ? ` · consultado às ${format(lastUpdate, 'HH:mm')}` : ''}
              </Typography>
            </Paper>
          </Grid>

          {/* Cards Secundários */}
          <Grid item xs={12} md={esconderLimite ? 6 : 4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'success.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TrendingUpIcon color="success" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Saldo Disponível
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {maskValue(formatCurrency(total))}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {esconderLimite ? 'Saldo total da conta' : 'Saldo + Limite de Crédito'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {!esconderLimite && (
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'info.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CreditCardIcon color="info" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Limite de Crédito
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {maskValue(formatCurrency(limite))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Cheque Especial disponível
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={esconderLimite ? 6 : 4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'warning.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LockIcon color="warning" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Saldo Bloqueado
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {maskValue(formatCurrency(bloqueado))}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Valores indisponíveis
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Ações Rápidas */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Ações Rápidas
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button fullWidth variant="outlined" href="/conta/extrato" sx={{ py: 1.5 }}>
                    Ver Extrato
                  </Button>
                </Grid>
                {features.pixEnviar && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button fullWidth variant="outlined" href="/pix/enviar" sx={{ py: 1.5 }}>
                      Enviar PIX
                    </Button>
                  </Grid>
                )}
                {features.pixCobranca && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button fullWidth variant="outlined" href="/pix/cobranca" sx={{ py: 1.5 }}>
                      Cobrar via PIX
                    </Button>
                  </Grid>
                )}
                {features.ted && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button fullWidth variant="outlined" href="/transfers/new" sx={{ py: 1.5 }}>
                      Transferir
                    </Button>
                  </Grid>
                )}
                {features.boletos && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button fullWidth variant="outlined" href="/pagamentos/pagar" sx={{ py: 1.5 }}>
                      Pagar Boleto
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
